import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { requestJson } from '../utils/lifecycle';

const CurrentUserContext = createContext(null);

// The backend decides the role from the CRM token; the browser never infers it from claims,
// so a stale or edited localStorage value cannot widen what the UI shows.
export function CurrentUserProvider({ children }) {
  const [state, setState] = useState({ status: 'loading', user: null, error: null });

  const load = useCallback(() => {
    let cancelled = false;
    setState({ status: 'loading', user: null, error: null });
    requestJson('/me')
      .then((user) => { if (!cancelled) setState({ status: 'ready', user, error: null }); })
      .catch((error) => {
        if (cancelled) return;
        setState({ status: error.status === 403 ? 'forbidden' : 'error', user: null, error });
      });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => load(), [load]);

  const value = useMemo(() => ({
    ...state,
    retry: load,
    can: (permission) => Boolean(state.user?.permissions?.includes(permission)),
  }), [state, load]);

  return <CurrentUserContext.Provider value={value}>{children}</CurrentUserContext.Provider>;
}

export function useCurrentUser() {
  const context = useContext(CurrentUserContext);
  if (!context) throw new Error('useCurrentUser must be used within CurrentUserProvider');
  return context;
}
